import React from 'react';
import { useLocale } from '../../contexts/LocaleContext';
import { cn } from '../../lib/utils';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';

export const ADMIN_PAGE_CLASSNAME = 'mx-auto w-full min-w-0 max-w-[1400px]';
export const ADMIN_PAGE_NARROW_CLASSNAME = 'mx-auto w-full min-w-0 max-w-[1200px]';
export const ADMIN_PAGE_MEDIUM_CLASSNAME = 'mx-auto w-full min-w-0 max-w-[1100px]';
export const ADMIN_PAGE_TITLE_CLASSNAME = 'mb-4 text-2xl font-bold text-foreground';
export const ADMIN_SECTION_TITLE_CLASSNAME = 'mb-3 text-lg font-semibold text-foreground';
export const ADMIN_LABEL_CLASSNAME = 'text-sm font-semibold text-muted-foreground';
export const ADMIN_FIELD_LABEL_CLASSNAME = 'mb-2 block text-sm font-medium text-foreground';
export const ADMIN_MUTED_TEXT_CLASSNAME = 'text-sm text-muted-foreground';
export const ADMIN_BODY_TEXT_CLASSNAME = 'text-sm text-foreground';
export const ADMIN_CHARTS_GRID_CLASSNAME = 'mb-8 grid w-full min-w-0 grid-cols-1 gap-8 lg:grid-cols-2';
export const ADMIN_FILTERS_GRID_CLASSNAME = 'grid w-full min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3';
export const ADMIN_STATS_ROW_CLASSNAME = 'mb-8 flex flex-wrap gap-4';
export const ADMIN_STAT_CARD_CLASSNAME = 'min-w-0 flex-1 rounded-lg border border-border bg-card p-4 sm:p-6';
export const ADMIN_STAT_LABEL_CLASSNAME = 'text-xs text-muted-foreground';
export const ADMIN_STAT_VALUE_CLASSNAME = 'text-2xl font-bold text-foreground';
export const ADMIN_PANEL_CLASSNAME = 'mb-8 min-w-0 rounded-lg border border-border bg-card p-4 sm:p-6';
export const ADMIN_PANEL_TITLE_CLASSNAME = 'mb-4 text-lg font-semibold text-foreground';
export const ADMIN_FILTER_BAR_CLASSNAME = 'mb-8 flex flex-wrap items-center gap-4 sm:justify-end';
export const ADMIN_CHART_PANEL_CLASSNAME = 'min-w-0 w-full max-w-[500px] flex-1 rounded-lg border border-border bg-card p-4';
export const ADMIN_TABLE_CLASSNAME = 'admin-data-table';
export const ADMIN_TABLE_ROW_CLICKABLE_CLASSNAME = 'admin-data-table-row-clickable';
export const ADMIN_TABLE_CELL_MUTED_CLASSNAME = 'admin-table-cell-muted';
export const ADMIN_TABLE_LINK_CLASSNAME = 'admin-table-link';
export const ADMIN_TABLE_SHELL_CLASSNAME = 'min-w-0 overflow-hidden rounded-lg border border-border bg-card';
export const ADMIN_TABLE_SCROLL_CLASSNAME = 'overflow-x-auto';
export const ADMIN_INLINE_LINK_BUTTON_CLASSNAME =
  'cursor-pointer border-0 bg-transparent p-0 text-sm text-primary underline-offset-2 hover:underline';
export const ADMIN_MODAL_OVERLAY_CLASSNAME =
  'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
export const ADMIN_MODAL_CLASSNAME =
  'max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-border bg-card p-6 text-foreground shadow-lg';
export const ADMIN_DIVIDER_CLASSNAME = 'mt-6 border-t border-border pt-6';
export const ADMIN_EMPTY_BOX_CLASSNAME =
  'rounded-lg border border-border bg-muted/40 p-8 text-center text-muted-foreground';

export const ADMIN_FILTER_CONTROL_CLASSNAME =
  'h-[38px] w-full max-w-full rounded-[10px] border border-input bg-card px-3 text-sm text-foreground shadow-sm sm:w-auto sm:min-w-[180px]';

export const ADMIN_INLINE_SELECT_CLASSNAME =
  'w-full max-w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground sm:w-auto sm:min-w-[180px]';

export const ADMIN_BADGE_BASE_CLASSNAME =
  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';
export const ADMIN_BADGE_NEUTRAL_CLASSNAME =
  `${ADMIN_BADGE_BASE_CLASSNAME} bg-muted text-muted-foreground`;
export const ADMIN_BADGE_SUCCESS_CLASSNAME =
  `${ADMIN_BADGE_BASE_CLASSNAME} bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200`;
export const ADMIN_BADGE_WARNING_CLASSNAME =
  `${ADMIN_BADGE_BASE_CLASSNAME} bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200`;
export const ADMIN_BADGE_DANGER_CLASSNAME =
  `${ADMIN_BADGE_BASE_CLASSNAME} bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200`;
export const ADMIN_BADGE_INFO_CLASSNAME =
  `${ADMIN_BADGE_BASE_CLASSNAME} bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200`;
export const ADMIN_BADGE_PURPLE_CLASSNAME =
  `${ADMIN_BADGE_BASE_CLASSNAME} bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200`;

export const ADMIN_CHART_TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    color: 'var(--foreground)',
  },
  labelStyle: {
    color: 'var(--foreground)',
  },
  itemStyle: {
    color: 'var(--foreground)',
  },
} as const;

export const AdminTableWrap: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn(ADMIN_TABLE_SHELL_CLASSNAME, className)}>
    <div className={ADMIN_TABLE_SCROLL_CLASSNAME}>{children}</div>
  </div>
);

export const AdminErrorAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Alert variant="destructive">
    <AlertDescription>{children}</AlertDescription>
  </Alert>
);

export const AdminEmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="py-12 text-center text-muted-foreground">{children}</p>
);

export const AdminLoadingState: React.FC<{ label?: string }> = ({ label }) => {
  const { t } = useLocale();
  return <p className="py-12 text-center text-muted-foreground">{label ?? t('loading')}</p>;
};

interface AdminPaginationBarProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalCount?: number;
  alwaysShow?: boolean;
}

export const AdminPaginationBar: React.FC<AdminPaginationBarProps> = ({
  page,
  totalPages,
  onPageChange,
  totalCount,
  alwaysShow = false,
}) => {
  const { t } = useLocale();
  if (!alwaysShow && totalPages <= 1) {
    return null;
  }

  return (
    <div className="mt-4 flex items-center justify-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
      >
        {t('previous')}
      </Button>
      <span className="px-3 text-sm text-muted-foreground">
        {page} / {totalPages}
        {totalCount != null ? ` (${totalCount} ${t('total')})` : ''}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
      >
        {t('next')}
      </Button>
    </div>
  );
};
